/**
 * Converting a chosen file to WebP in the browser, before it is uploaded.
 *
 * Merchants upload whatever their camera, their phone or their supplier gave
 * them, which is usually a JPEG and occasionally a PNG screenshot. Storing that
 * as it arrived means the bytes kept as the editable source - the copy a later
 * crop is made from, and the one served when the server could not resize the
 * image - stay in the format nobody chose. Re-encoding here makes WebP the
 * format of record from the moment the file leaves the merchant's machine:
 * the object in the bucket, its extension and its media type all agree.
 *
 * The conversion is a courtesy, never a gate. A browser that cannot decode the
 * file or cannot encode WebP hands back the original untouched, and the upload
 * proceeds exactly as it did before - the server still builds its own WebP
 * ladder from whatever it receives. That is also what happens under jsdom,
 * where there is no canvas at all.
 */

export const WEBP_TYPE = 'image/webp';

/**
 * Higher than the 82 the server's resized copies use. This is the source that
 * a later crop is decoded from, so it is worth keeping closer to the original
 * than to the smallest copy the storefront serves.
 */
const WEBP_QUALITY = 0.92;

/** The same name, carrying the extension the bytes now deserve. */
export function webpName(name: string): string {
  const base = name.replace(/\.[^.]+$/, '').trim();
  return `${base || 'image'}.webp`;
}

/**
 * Whether a GIF has more than one frame.
 *
 * A canvas only ever sees the first frame, so converting an animated GIF would
 * quietly throw the animation away. The server already declines to resize
 * these for the same reason and serves them as uploaded; this leaves them
 * alone so that stays true.
 *
 * Each rendered frame is introduced by a Graphic Control Extension - the bytes
 * `21 F9 04`. Pixel data can coincidentally spell that, so a still image can be
 * misread as animated; the cost of that is one file uploaded in its original
 * format, which is the side to be wrong on.
 *
 * Exported for `imageProcessor`, which has to leave the same files alone.
 */
export async function isAnimatedGif(file: Blob): Promise<boolean> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let frames = 0;
  for (let index = 0; index + 2 < bytes.length; index += 1) {
    if (bytes[index] === 0x21 && bytes[index + 1] === 0xf9 && bytes[index + 2] === 0x04) {
      frames += 1;
      if (frames > 1) return true;
    }
  }
  return false;
}

/**
 * Repaints the file at its own size and re-encodes it, or `null` if this
 * browser cannot.
 *
 * Drawing at the bitmap's natural size is what preserves the aspect ratio and
 * the detail: nothing is scaled, cropped or padded here. Resizing is the edit
 * dialog's job and stays a deliberate act.
 */
async function encodeWebp(file: File): Promise<Blob | null> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null;

  const bitmap = await createImageBitmap(file);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d');
    if (!context || typeof canvas.toBlob !== 'function') return null;
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, WEBP_TYPE, WEBP_QUALITY),
    );
    // A canvas asked for a format it cannot write answers with a PNG rather
    // than refusing, so the type it came back as is what decides.
    return blob?.type === WEBP_TYPE ? blob : null;
  } finally {
    bitmap.close?.();
  }
}

/**
 * The file as WebP, or the file itself when converting it would lose something
 * or the browser will not do it.
 */
export async function toWebpFile(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === WEBP_TYPE) return file;

  try {
    if (file.type === 'image/gif' && (await isAnimatedGif(file))) return file;
    const converted = await encodeWebp(file);
    if (!converted) return file;
    return new File([converted], webpName(file.name), {
      type: WEBP_TYPE,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}
